import React, { useEffect, useState, useCallback } from "react";
import { PageContainer } from "global-styles/global.styles";
import { withAuthentication, withEmailVerification } from "utils/RouterUtils";
import { Text, Button, Callout } from "@appsmith/ads";
import { createMessage, SECURITY_REPORTS_HEADING } from "constants/Messages";
import API from "api/Api";
import styled from "styled-components";

interface ReportItem {
  imageTag: string;
  scanDate: string;
  totalCritical: number;
  totalHigh: number;
  pdfSha256: string;
  pdfSizeBytes: number;
  hasPdf: boolean;
  hasOpenvex: boolean;
  recommended: boolean;
}

interface ReportsResponse {
  currentVersions: string[];
  unavailableVersions: string[];
  reports: ReportItem[];
}

interface CompareItem {
  cveId: string;
  changeType: string;
  severity: string;
  packageName: string;
  component: string;
  oldPackageVersion: string | null;
  newPackageVersion: string | null;
  oldVexStatus: string | null;
  newVexStatus: string | null;
  oldAssessment: string | null;
  newAssessment: string | null;
}

interface CompareResponse {
  oldImageTag: string;
  newImageTag: string;
  summary: Record<string, number>;
  findings: CompareItem[];
}

const ReportCard = styled.div<{
  isRecommended: boolean;
  isSelected: boolean;
}>`
  border: 2px solid
    ${({ isRecommended, isSelected }) =>
      isSelected
        ? "#3b82f6"
        : isRecommended
        ? "var(--ads-v2-color-border-brand)"
        : "var(--ads-v2-color-border)"};
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 12px;
  background: ${({ isRecommended, isSelected }) =>
    isSelected
      ? "#eff6ff"
      : isRecommended
      ? "var(--ads-v2-color-bg-subtle)"
      : "var(--ads-v2-color-bg)"};
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  ${({ isSelected }) => isSelected && "box-shadow: 0 0 0 3px #bfdbfe;"}
  &:hover {
    border-color: ${({ isSelected }) => (isSelected ? "#3b82f6" : "#94a3b8")};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const CompareCheckbox = styled.label<{
  checked: boolean;
  disabled: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${({ checked, disabled }) =>
    disabled ? "#cbd5e1" : checked ? "#1d4ed8" : "#64748b"};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  user-select: none;
  white-space: nowrap;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  .checkbox-box {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    border: 2px solid
      ${({ checked, disabled }) =>
        disabled ? "#e2e8f0" : checked ? "#3b82f6" : "#cbd5e1"};
    background: ${({ checked, disabled }) =>
      disabled ? "#f1f5f9" : checked ? "#3b82f6" : "#fff"};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.12s;
  }
  .checkbox-box svg {
    width: 10px;
    height: 10px;
    stroke: #fff;
    stroke-width: 3;
    fill: none;
  }
  &:hover .checkbox-box {
    border-color: ${({ disabled }) => (disabled ? "#e2e8f0" : "#3b82f6")};
  }
`;

const TagRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

type BadgeSeverity =
  | "critical"
  | "high"
  | "info"
  | "success"
  | "warning"
  | "blue";

const Badge = styled.span<{ severity: BadgeSeverity }>`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  ${({ severity }) =>
    severity === "critical"
      ? "background: #fde8e8; color: #c53030;"
      : severity === "high"
      ? "background: #fff3e0; color: #e65100;"
      : severity === "success"
      ? "background: #e8f5e9; color: #2e7d32;"
      : severity === "warning"
      ? "background: #fff8e1; color: #f57f17;"
      : severity === "blue"
      ? "background: #dbeafe; color: #1d4ed8;"
      : "background: #e8f5e9; color: #2e7d32;"}
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 14px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 24px;
  color: var(--ads-v2-color-fg-muted);
`;

const CompareBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 12px 20px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ComparePanel = styled.div`
  border: 1px solid var(--ads-v2-color-border);
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  background: var(--ads-v2-color-bg);
`;

const SummaryGrid = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  border: 1px solid var(--ads-v2-color-border);
  border-radius: 6px;
  padding: 12px 20px;
  text-align: center;
  min-width: 120px;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  border: 1px solid var(--ads-v2-color-border);
  border-radius: 6px;
`;

const FindingsTable = styled.table`
  width: 100%;
  min-width: 820px;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 13px;

  th,
  td {
    padding: 8px 10px;
    text-align: center;
    vertical-align: middle;
    border-bottom: 1px solid var(--ads-v2-color-border);
    overflow: hidden;
    text-overflow: ellipsis;
  }
  th {
    font-weight: 600;
    background: var(--ads-v2-color-bg-subtle);
    white-space: nowrap;
  }
  tr:last-child td {
    border-bottom: none;
  }

  /* Column widths — fixed layout */
  col.col-cve {
    width: 140px;
  }
  col.col-change {
    width: 130px;
  }
  col.col-severity {
    width: 80px;
  }
  col.col-package {
    width: auto;
  }
  col.col-version {
    width: 100px;
  }
  col.col-status {
    width: 110px;
  }

  td.cell-cve {
    white-space: nowrap;
    font-family: "SF Mono", "Menlo", monospace;
    font-size: 12px;
  }
  td.cell-package {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  td.cell-version {
    white-space: nowrap;
    font-family: "SF Mono", "Menlo", monospace;
    font-size: 12px;
  }
  td.cell-status {
    white-space: nowrap;
  }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button<{ active: boolean }>`
  border: 1px solid
    ${({ active }) => (active ? "#3b82f6" : "var(--ads-v2-color-border)")};
  background: ${({ active }) =>
    active ? "#dbeafe" : "var(--ads-v2-color-bg)"};
  color: ${({ active }) => (active ? "#1d4ed8" : "inherit")};
  border-radius: 6px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: ${({ active }) => (active ? "600" : "400")};
  cursor: pointer;
  transition: all 0.12s;
  &:hover {
    border-color: #3b82f6;
    background: #eff6ff;
  }
`;

type ChangeType =
  | "ALL"
  | "NEW"
  | "SOLVED"
  | "VERSION_CHANGED"
  | "STATUS_CHANGED"
  | "UNCHANGED";

function SecurityReports() {
  const [data, setData] = useState<ReportsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const [selected, setSelected] = useState<string[]>([]);
  const [comparing, setComparing] = useState(false);
  const [compareResult, setCompareResult] = useState<CompareResponse | null>(
    null,
  );
  const [filterType, setFilterType] = useState<ChangeType>("ALL");

  useEffect(() => {
    setLoading(true);
    API.get("/security-reports")
      .then((res) => {
        setData((res as any)?.data || null);
        setError(null);
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.responseMeta?.error?.message ||
          "Failed to load security reports";
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = useCallback(
    async (imageTag: string, format: "pdf" | "openvex") => {
      const key = `${imageTag}-${format}`;
      setDownloading(key);
      try {
        const res = await API.get(
          `/security-reports/${format}`,
          { imageTag },
          { responseType: format === "pdf" ? "blob" : "text" },
        );
        const payload: any = res;
        const content =
          typeof payload === "string" ? payload : JSON.stringify(payload);
        const blob =
          format === "pdf"
            ? new Blob([payload], { type: "application/pdf" })
            : new Blob([content], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download =
          format === "pdf"
            ? `appsmith-cvh3-${imageTag}.pdf`
            : `appsmith-cvh3-${imageTag}.openvex.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (e: any) {
        const msg =
          e?.response?.data?.responseMeta?.error?.message ||
          `Failed to download ${format.toUpperCase()} for ${imageTag}`;
        setError(msg);
      } finally {
        setDownloading(null);
      }
    },
    [],
  );

  const toggleSelection = useCallback((imageTag: string) => {
    setSelected((prev) => {
      if (prev.includes(imageTag)) {
        return prev.filter((t) => t !== imageTag);
      }
      if (prev.length >= 2) {
        return [prev[1], imageTag];
      }
      return [...prev, imageTag];
    });
  }, []);

  const handleCompare = useCallback(async () => {
    if (selected.length !== 2) return;
    setComparing(true);
    setError(null);
    setCompareResult(null);
    try {
      const res = await API.get("/security-reports/compare", {
        oldImageTag: selected[0],
        newImageTag: selected[1],
      });
      setCompareResult((res as any)?.data || null);
      setFilterType("ALL");
    } catch (e: any) {
      const msg =
        e?.response?.data?.responseMeta?.error?.message ||
        "Failed to compare versions";
      setError(msg);
    } finally {
      setComparing(false);
    }
  }, [selected]);

  const clearCompare = useCallback(() => {
    setCompareResult(null);
    setSelected([]);
    setFilterType("ALL");
  }, []);

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  const filteredFindings = compareResult?.findings.filter(
    (f) => filterType === "ALL" || f.changeType === filterType,
  );

  const changeTypeLabel: Record<string, string> = {
    NEW: "Newly Introduced",
    SOLVED: "Resolved",
    VERSION_CHANGED: "Version Changed",
    STATUS_CHANGED: "Status Changed",
    UNCHANGED: "Unchanged",
  };

  const changeTypeBadge: Record<string, BadgeSeverity> = {
    NEW: "critical",
    SOLVED: "success",
    VERSION_CHANGED: "warning",
    STATUS_CHANGED: "high",
    UNCHANGED: "info",
  };

  return (
    <PageContainer>
      <Text className="!font-semibold !mb-2" kind="heading-l" renderAs="p">
        {createMessage(SECURITY_REPORTS_HEADING)}
      </Text>
      <Text className="!mb-6" kind="body-m" renderAs="p">
        Download vulnerability assessment reports for your Appsmith version.
        Each report evaluates unresolved Critical and High severity CVEs against
        Appsmith&apos;s supported self-hosted deployment.
      </Text>

      {error && (
        <Callout className="!mb-4" kind="error">
          {error}
        </Callout>
      )}

      {!loading &&
        data &&
        data.unavailableVersions &&
        data.unavailableVersions.length > 0 && (
          <Callout className="!mb-4" kind="warning">
            {`No security report is published yet for the version(s) you're running: ${data.unavailableVersions.join(
              ", ",
            )}. Reports are added per Appsmith release — please check back later.`}
          </Callout>
        )}

      {/* Compare bar — only visible when user has started selecting */}
      {!loading && selected.length > 0 && (
        <CompareBar>
          <Text kind="body-m" renderAs="span">
            {selected.length === 1
              ? `Selected: ${selected[0]} — click another version to compare`
              : `Compare: ${selected[0]} → ${selected[1]}`}
          </Text>
          <ButtonRow>
            {selected.length === 2 && (
              <Button
                isLoading={comparing}
                kind="primary"
                onClick={handleCompare}
                size="sm"
              >
                Compare CVEs
              </Button>
            )}
            <Button kind="tertiary" onClick={clearCompare} size="sm">
              Clear selection
            </Button>
          </ButtonRow>
        </CompareBar>
      )}

      {/* Compare results */}
      {compareResult && (
        <ComparePanel>
          <Text className="!font-semibold !mb-4" kind="heading-m" renderAs="p">
            CVE Comparison: {compareResult.oldImageTag} →{" "}
            {compareResult.newImageTag}
          </Text>
          <SummaryGrid>
            {Object.entries(compareResult.summary).map(([type, count]) => (
              <SummaryCard key={type}>
                <Text className="!font-semibold" kind="heading-s" renderAs="p">
                  {count}
                </Text>
                <Badge severity={changeTypeBadge[type] || "info"}>
                  {changeTypeLabel[type] || type}
                </Badge>
              </SummaryCard>
            ))}
          </SummaryGrid>

          <FilterRow>
            {(
              [
                "ALL",
                "NEW",
                "SOLVED",
                "VERSION_CHANGED",
                "STATUS_CHANGED",
                "UNCHANGED",
              ] as ChangeType[]
            ).map((type) => (
              <FilterButton
                active={filterType === type}
                key={type}
                onClick={() => setFilterType(type)}
              >
                {type === "ALL" ? "All" : changeTypeLabel[type] || type}
                {type !== "ALL" && compareResult.summary[type] != null
                  ? ` (${compareResult.summary[type]})`
                  : ""}
              </FilterButton>
            ))}
          </FilterRow>

          {filteredFindings && filteredFindings.length > 0 ? (
            <TableWrapper>
              <FindingsTable>
                <colgroup>
                  <col className="col-cve" />
                  <col className="col-change" />
                  <col className="col-severity" />
                  <col className="col-package" />
                  <col className="col-version" />
                  <col className="col-version" />
                  <col className="col-status" />
                  <col className="col-status" />
                </colgroup>
                <thead>
                  <tr>
                    <th>CVE ID</th>
                    <th>Change</th>
                    <th>Severity</th>
                    <th>Package</th>
                    <th>Old Version</th>
                    <th>New Version</th>
                    <th>Old Status</th>
                    <th>New Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFindings.map((f, idx) => (
                    <tr key={`${f.cveId}-${f.packageName}-${idx}`}>
                      <td className="cell-cve">{f.cveId}</td>
                      <td>
                        <Badge
                          severity={changeTypeBadge[f.changeType] || "info"}
                        >
                          {changeTypeLabel[f.changeType] || f.changeType}
                        </Badge>
                      </td>
                      <td>{f.severity}</td>
                      <td className="cell-package" title={f.packageName}>
                        {f.packageName}
                      </td>
                      <td className="cell-version">
                        {f.oldPackageVersion || "—"}
                      </td>
                      <td className="cell-version">
                        {f.newPackageVersion || "—"}
                      </td>
                      <td className="cell-status">{f.oldVexStatus || "—"}</td>
                      <td className="cell-status">{f.newVexStatus || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </FindingsTable>
            </TableWrapper>
          ) : (
            <Text kind="body-m" renderAs="p">
              No findings match this filter.
            </Text>
          )}

          <ButtonRow>
            <Button kind="tertiary" onClick={clearCompare} size="sm">
              Close Comparison
            </Button>
          </ButtonRow>
        </ComparePanel>
      )}

      {loading && (
        <Text className="!py-8" kind="body-m" renderAs="p">
          Loading reports...
        </Text>
      )}

      {!loading && data && data.reports.length === 0 && (
        <EmptyState>
          <Text kind="heading-s" renderAs="p">
            No published reports available yet
          </Text>
          <Text className="!mt-2" kind="body-m" renderAs="p">
            Security reports are generated for each Appsmith release. Check back
            after your next version scan completes.
          </Text>
        </EmptyState>
      )}

      {!loading &&
        data &&
        data.reports.map((report) => (
          <ReportCard
            isRecommended={report.recommended}
            isSelected={selected.includes(report.imageTag)}
            key={report.imageTag}
            onClick={() => toggleSelection(report.imageTag)}
          >
            <CardHeader>
              <TagRow>
                <Text
                  className="!font-semibold"
                  kind="heading-s"
                  renderAs="span"
                >
                  {report.imageTag}
                </Text>
                {report.recommended && (
                  <Badge severity="info">Your version</Badge>
                )}
                {selected.includes(report.imageTag) && (
                  <Badge severity="blue">
                    {selected.indexOf(report.imageTag) === 0
                      ? "\u2460 Base"
                      : "\u2461 Target"}
                  </Badge>
                )}
              </TagRow>
              <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Text kind="body-s" renderAs="span">
                  Scanned {report.scanDate}
                </Text>
                <CompareCheckbox
                  checked={selected.includes(report.imageTag)}
                  disabled={
                    selected.length >= 2 && !selected.includes(report.imageTag)
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    if (
                      selected.length < 2 ||
                      selected.includes(report.imageTag)
                    ) {
                      toggleSelection(report.imageTag);
                    }
                  }}
                >
                  <span className="checkbox-box">
                    {selected.includes(report.imageTag) && (
                      <svg viewBox="0 0 12 12">
                        <polyline points="2,6 5,9 10,3" />
                      </svg>
                    )}
                  </span>
                  Compare
                </CompareCheckbox>
              </span>
            </CardHeader>
            <TagRow>
              {report.totalCritical > 0 && (
                <Badge severity="critical">
                  {report.totalCritical} Critical
                </Badge>
              )}
              <Badge severity="high">{report.totalHigh} High</Badge>
              {report.hasPdf && (
                <Text kind="body-s" renderAs="span">
                  PDF: {formatBytes(report.pdfSizeBytes)}
                </Text>
              )}
            </TagRow>
            <ButtonRow>
              {report.hasPdf && (
                <Button
                  isLoading={downloading === `${report.imageTag}-pdf`}
                  kind="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(report.imageTag, "pdf");
                  }}
                  size="sm"
                  startIcon="download"
                >
                  Download PDF
                </Button>
              )}
              {report.hasOpenvex && (
                <Button
                  isLoading={downloading === `${report.imageTag}-openvex`}
                  kind="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(report.imageTag, "openvex");
                  }}
                  size="sm"
                  startIcon="download"
                >
                  Download OpenVEX
                </Button>
              )}
            </ButtonRow>
          </ReportCard>
        ))}
    </PageContainer>
  );
}

export default withAuthentication(withEmailVerification(SecurityReports));
